import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Box, Divider, Grid } from '@material-ui/core';
import { formatBytes, getLocalDateTime } from '../../../Utils/GlobalUtils';
import PrimaryModelBtn from '../../Files/PrimaryModelBtn';
import { useDispatch } from 'react-redux';
import { setModelFileAsPrimary } from '../../../redux/actions/manageAssetActions';
import DownloadUrl from '../OutPut/DownloadUrl';
import FileRebuildBtn from './FileRebuildBtn';
import ArtifactAccordion from '../OutPut/ArtifactAccordion';
import SourceViewerDialog from '../../common/ApprovalViewer/SourceViewerDialog';
import AlignmentSelect from './AlignmentSelect';
import PublishedArtifactsWarningDrawer from '../PublishedArtifactsWarningDialog';
import { useState } from 'react';
import { assetHasPublishedArtifacts } from '../../../Utils/AssetUtils';
import ExpressQABtn from '../../Files/ExpressQABtn';
import SignedUrlCopyBtn from './SignedUrlCopyBtn';

const useStyles = makeStyles((theme) => ({
	root: {
		width: '100%',
	},
	accordion: {
		backgroundColor: '#F5F5F5',
	},
	heading: {
		fontSize: theme.typography.pxToRem(15),
		fontWeight: theme.typography.fontWeightRegular,
	},
	card: {
		flexGrow: 1,
	},
	paper: {
		padding: theme.spacing(3),
		margin: '10px',
		maxWidth: 700,
		minWidth: 600,
	},
	headerTypography: {
		textTransform: 'uppercase',
		fontWeight: theme.typography.fontWeightBold,
	},
	textTransformNone: {
		textTransform: 'none',
	},
	fileName: {
		wordBreak: 'break-all',
	},
	field: {
		fontWeight: 'bold',
	},
	artifactContainer: {
		maxHeight: '50em',
		overflowY: 'auto',
		overflowX: 'hidden',
	},
}));

const CardTypography = ({ field, val }) => {
	const classes = useStyles();
	return (
		<Typography variant="body1">
			<span className={classes.field}>{field}</span>: {val}
		</Typography>
	);
};
const SourceFileCard = ({ asset, file }) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const { id, url, size, isPrimary, type, createdAt, updatedAt, imageUrl } = file;
	const createdDate = getLocalDateTime(createdAt);
	const updatedDate = getLocalDateTime(updatedAt);
	const [open, setOpen] = useState(false);
	// ES returns a flatten object of artifacts at same level as sourceFiles. regroup artifacts for corresponding srcFile.
	const srcFileArtifacts = asset.artifacts.filter((a) => a.sourceFileId === file.id);
	const handleDrawerBtnClick = () => {
		setOpen(false);
		dispatch(setModelFileAsPrimary(file, asset));
	};
	const drawerProps = {
		open,
		setOpen,
		primaryBtnClick: handleDrawerBtnClick,
	};

	const handlePrimaryBtnClick = () => {
		// prevent any action when the model file is already set as primary.
		if (!isPrimary) {
			// Throw a warning dialog, before changing primary source file.
			if (assetHasPublishedArtifacts(asset)) {
				setOpen(true);
			} else {
				dispatch(setModelFileAsPrimary(file, asset));
			}
		}
	};
	return (
		<div className={classes.card}>
			<Grid container>
				<Grid item xs={3}>
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<Typography>Source Model</Typography>
						</Grid>
						<Grid item xs={12}>
							<Grid container alignItems="baseline">
								<PrimaryModelBtn isPrimary={isPrimary} onClick={handlePrimaryBtnClick} />
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<SourceViewerDialog
								label={`Source - ${type}`}
								modelUrl={url}
								size={formatBytes(size)}
								type={type}
								name={id}
								createdDate={createdDate}
								imageUrl={imageUrl}
							/>
						</Grid>
						<Grid item xs={12}>
							<Typography className={classes.headerTypography}>{type}</Typography>
						</Grid>
						<Grid item xs={12}>
							<CardTypography field={'Size'} val={formatBytes(size)} />
						</Grid>
						<Grid item xs={12}>
							<CardTypography field={'Uploaded Date'} val={createdDate} />
						</Grid>
						<Grid item xs={12}>
							<CardTypography field={'Updated Date'} val={updatedDate} />
						</Grid>
						<Grid item xs={12}>
							{/* Alignment dropdown is only for primary source files */}
							{isPrimary ? <AlignmentSelect asset={asset} /> : ''}
						</Grid>
						<Grid item xs={12}>
							<DownloadUrl downloadUrl={url} isBtnDisabled={false} toolTipMsg="" label="glb"></DownloadUrl>
							<FileRebuildBtn asset={asset} srcFile={file} />
							<ExpressQABtn assetId={asset.id} />
							<SignedUrlCopyBtn permalink={url} />
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={9} className={classes.artifactContainer}>
					<ArtifactAccordion asset={asset} srcFile={file} artifacts={srcFileArtifacts} />
				</Grid>
				{open ? <PublishedArtifactsWarningDrawer {...drawerProps} /> : null}
			</Grid>
		</div>
	);
};
const AccordionSummaryTxt = ({ sourceFile }) => {
	const { filename, isPrimary } = sourceFile;
	const label = isPrimary ? 'Primary Model: ' : 'Source Model: ';
	return (
		<Typography variant="body1">
			<span style={{ fontWeight: 'bold' }}>{label}</span>
			{filename}
		</Typography>
	);
};
// Source accordion component, to display all info related to source and artifacts of the source.
export default function SourceAccordion({ sourceFile, asset }) {
	const classes = useStyles();
	const [expanded, setExpanded] = React.useState(true);
	const handleChange = () => {
		setExpanded(!expanded);
	};
	return (
		<div className={classes.root}>
			<Accordion className={classes.accordion} expanded={expanded} onChange={handleChange}>
				<AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="source-content" id="source-content">
					<AccordionSummaryTxt sourceFile={sourceFile} />
				</AccordionSummary>
				<Divider />
				<AccordionDetails>
					<SourceFileCard asset={asset} file={sourceFile} />
				</AccordionDetails>
			</Accordion>
			<Box m={2} />
		</div>
	);
}
